import React, { useRef, useState, useEffect } from 'react';
import Slider from 'react-slick';
import Text from 'components/Core/Text';
import useBreakpointView from 'utils/useBreakpointView';
import ContentfulRichText from 'components/RichText/ContentfulRichText';
import { colorTypeHelper } from 'utils/colorUtility';
import CustomCarouselPaging from './CustomCarouselPaging';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import leftArrowDesktop from './images/left-arrow-desktop.svg';
import rightArrowDesktop from './images/right-arrow-desktop.svg';
import leftArrowMobile from './images/left-arrow-mobile.svg';
import rightArrowMobile from './images/right-arrow-mobile.svg';
import { Container } from './styles';

const Carousel = ({
  activeIndex,
  heading,
  subhead,
  versionStyling,
  children,
  bgColor,
  includesFullWidthBackground,
  headerItems,
  contentful_id: contentfulID,
  ...otherProps
}) => {
  const sliderRef = useRef();
  const containerRef = useRef();
  const [update, triggerUpdate] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const isMobile = useBreakpointView(['xs']);

  const baseSettings = {
    dots: versionStyling !== 'V2',
    adaptiveHeight: versionStyling === 'V3',
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    autoplay: false,
    draggable: true,
    swipeToSlide: false,
    speed: 500,
    arrows: false,
    fade: true,
    afterChange: (current) => {
      triggerUpdate(!update);
      setCurrentPage(current);
    },
  };

  const v1SliderSettings = {
    ...baseSettings,
    arrows: true,
    fade: false,
    dots: false,
    nextArrow: (
      <div className="slider-next-arrow">
        <img src={rightArrowDesktop} className="min-md" alt="arrow" />
        <img src={rightArrowMobile} className="max-md" alt="arrow" />
      </div>
    ),
    prevArrow: (
      <div className="slider-prev-arrow">
        <img src={leftArrowDesktop} className="min-md" alt="arrow" />
        <img src={leftArrowMobile} className="max-md" alt="arrow" />
      </div>
    ),
  };

  const v5SliderSettings = {
    ...v1SliderSettings,
    slidesToShow: 3.1,
    arrows: false,
    infinite: false,
    responsive: [
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          adaptiveHeight: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2.8,
        },
      },
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 2.9,
        },
      },
    ],
  };

  const sliderSettings =
    !versionStyling || versionStyling === 'V1'
      ? v1SliderSettings
      : versionStyling === 'V5'
      ? v5SliderSettings
      : baseSettings;

  useEffect(() => {
    if (sliderRef?.current?.props.slidesToShow === 1) {
      return;
    }
    const maxHeight = [...containerRef.current.querySelectorAll('.slick-slide')].reduce(
      (prev, cur) => (cur.offsetHeight > prev ? cur.offsetHeight : prev),
      0,
    );

    if (maxHeight > 0) {
      containerRef.current.querySelector('.slick-list').style.height = `${maxHeight}px`;
    }
  }, [containerRef.current]);

  useEffect(() => {
    if (activeIndex) {
      const desktopAdvance = !isMobile && activeIndex > 2;
      const mobileAdvance = isMobile && activeIndex > 0;

      setTimeout(() => {
        if (desktopAdvance) {
          sliderRef?.current?.slickNext();
        } else if (mobileAdvance) {
          sliderRef?.current?.slickGoTo(activeIndex);
        }
      }, 1000);
    }
  }, [activeIndex]);

  return (
    <Container
      ref={containerRef}
      backgroundColor={colorTypeHelper[bgColor]}
      versionStyling={versionStyling || 'V1'}
      includesFullWidthBackground={includesFullWidthBackground}
      id={!heading?.raw ? heading?.replace(/\s+/g, '-').toLowerCase() : null}
      {...otherProps}
    >
      {heading?.raw ? (
        <ContentfulRichText
          className="carousel--heading"
          data-testid="carousel-title"
          richText={{ content: heading }}
        />
      ) : (
        heading && (
          <Text
            className="carousel--heading"
            data-testid="carousel-title"
            type="p"
            size={['V2', 'V3', 'V5'].includes(versionStyling) ? 'h2' : 'h3'}
          >
            {heading}
          </Text>
        )
      )}
      {subhead && (
        <ContentfulRichText
          className="carousel--subhead"
          data-testid="carousel-subhead"
          richText={{ content: subhead }}
        />
      )}
      {['V2', 'V3', 'V5', 'Bender'].includes(versionStyling) && (
        <CustomCarouselPaging
          versionStyling={versionStyling}
          sliderRef={sliderRef}
          contentful_id={contentfulID}
          currentPage={currentPage}
          headerItems={headerItems}
          infinite={sliderSettings.infinite}
        />
      )}
      <Slider ref={sliderRef} {...sliderSettings}>
        {children}
      </Slider>
    </Container>
  );
};

export default Carousel;
